import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, X, RotateCcw } from 'lucide-react'
import { aiApi } from '@/api/ai'
import { Button } from '@/components/ui/Button'
import { ExerciseActionHint } from '@/components/exercise/ExerciseActionHint'
import { FillBlankQuestion } from '@/components/tests/FillBlankQuestion'
import { MultipleChoiceQuestion } from '@/components/tests/MultipleChoiceQuestion'
import { LessonContextPassage } from '@/components/ai/LessonContextPassage'
import { flattenLesson, isExerciseCorrect } from '@/lib/practiceLesson'
import {
  clearLessonSession,
  estimateMinutesRemaining,
  saveLessonSession,
  type LessonSessionState,
} from '@/lib/lessonSessionStorage'
import { studyDurationMinutes } from '@/lib/studyDuration'
import type { FlatLessonStep, PracticeLesson, LessonExercise } from '@/types/practiceLesson'
import { copy } from '@/lib/copy'
import { useActionGate } from '@/lib/useActionGate'
import { cn } from '@/components/ui/cn'

interface ExerciseSetRunnerProps {
  lesson: PracticeLesson
  model?: string
  userId: string
  exerciseSetId?: string | null
  initialSession?: LessonSessionState | null
  onNewLesson: () => void
  onInProgressChange?: (inProgress: boolean) => void
  onLessonComplete?: () => void
  /** Mobile immersive layout: single column, full-width CTAs, no nested cards. */
  layout?: 'desktop' | 'mobile'
  onBack?: () => void
  className?: string
}

type StepResult = {
  checked: boolean
  correct: boolean | null
}

function stepsFromSession(lesson: PracticeLesson, session: LessonSessionState): FlatLessonStep[] {
  const all = flattenLesson(lesson)
  const byId = new Map(all.map((step) => [step.exercise.id, step]))
  return session.stepExerciseIds
    .map((id, index) => {
      const step = byId.get(id)
      return step ? { ...step, stepIndex: index } : null
    })
    .filter((step): step is FlatLessonStep => step != null)
}

function buildSessionSnapshot(
  exerciseSetId: string,
  lesson: PracticeLesson,
  model: string | undefined,
  activeSteps: FlatLessonStep[],
  currentIndex: number,
  answers: Record<string, string>,
  results: Record<string, StepResult>,
  phase: 'active' | 'summary',
  startedAt: number,
  activeStudyMs: number,
): Omit<LessonSessionState, 'userId' | 'savedAt'> {
  return {
    exerciseSetId,
    lesson,
    model,
    stepExerciseIds: activeSteps.map((s) => s.exercise.id),
    currentIndex,
    answers,
    results,
    phase,
    startedAt,
    activeStudyMs,
  }
}

export function ExerciseSetRunner({
  lesson,
  model,
  userId,
  exerciseSetId,
  initialSession,
  onNewLesson,
  onInProgressChange,
  onLessonComplete,
  layout = 'desktop',
  onBack,
  className,
}: ExerciseSetRunnerProps) {
  const isMobile = layout === 'mobile'
  const allSteps = useMemo(() => flattenLesson(lesson), [lesson])

  const restored = useMemo(() => {
    if (!initialSession) return null
    const steps = stepsFromSession(lesson, initialSession)
    if (steps.length === 0) return null
    return {
      activeSteps: steps,
      currentIndex: Math.min(initialSession.currentIndex, steps.length - 1),
      answers: initialSession.answers,
      results: initialSession.results,
      phase: initialSession.phase,
      // Resume active time only — do not continue wall-clock from the original start.
      startedAt: Date.now(),
      activeStudyMs: initialSession.activeStudyMs ?? 0,
    }
  }, [initialSession, lesson])

  const [activeSteps, setActiveSteps] = useState<FlatLessonStep[]>(restored?.activeSteps ?? allSteps)
  const [currentIndex, setCurrentIndex] = useState(restored?.currentIndex ?? 0)
  const [answers, setAnswers] = useState<Record<string, string>>(restored?.answers ?? {})
  const [results, setResults] = useState<Record<string, StepResult>>(restored?.results ?? {})
  const [phase, setPhase] = useState<'active' | 'summary'>(restored?.phase ?? 'active')
  const [startedAt, setStartedAt] = useState(restored?.startedAt ?? Date.now())
  const [activeStudyMs, setActiveStudyMs] = useState(restored?.activeStudyMs ?? 0)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitAttempt, setSubmitAttempt] = useState(0)
  const [mobilePane, setMobilePane] = useState<'scenario' | 'questions'>(() => {
    if (layout !== 'mobile' || !lesson.contextPassage) {
      return 'questions'
    }
    if (initialSession && (initialSession.currentIndex > 0 || initialSession.phase === 'summary')) {
      return 'questions'
    }
    return 'scenario'
  })
  const submittedRef = useRef(false)
  const progressSyncRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const activeStudyMsRef = useRef(activeStudyMs)
  const startedAtRef = useRef(startedAt)
  const studyPausedRef = useRef(false)
  const { hint: actionHint, clearHint, showHint } = useActionGate()

  const current = activeSteps[currentIndex]
  const total = activeSteps.length
  const currentResult = current ? results[current.exercise.id] : undefined
  const completedSteps = useMemo(() => {
    let count = 0
    for (let i = 0; i < activeSteps.length; i += 1) {
      const id = activeSteps[i].exercise.id
      if (!results[id]?.checked) break
      count += 1
    }
    return count
  }, [activeSteps, results])

  const displayExerciseNumber = Math.min(total, Math.max(1, completedSteps + (phase === 'summary' ? 0 : 1)))
  const progressPercent = total > 0 ? Math.round((completedSteps / total) * 100) : 0
  const minutesRemaining = estimateMinutesRemaining(lesson.estimatedMinutes, total, completedSteps)
  const correctCount = Object.values(results).filter((r) => r.correct === true).length
  const wrongSteps = activeSteps.filter((step) => results[step.exercise.id]?.correct === false)

  useEffect(() => {
    onInProgressChange?.(phase === 'active' && total > 0 && completedSteps < total)
  }, [phase, total, completedSteps, onInProgressChange])

  useEffect(() => {
    if (phase === 'summary') {
      clearLessonSession(userId)
      return
    }
    if (!exerciseSetId) {
      return
    }
    const snapshot = buildSessionSnapshot(
      exerciseSetId,
      lesson,
      model,
      activeSteps,
      currentIndex,
      answers,
      results,
      phase,
      startedAt,
      activeStudyMs,
    )
    saveLessonSession(userId, snapshot)
    if (progressSyncRef.current) {
      clearTimeout(progressSyncRef.current)
    }
    progressSyncRef.current = setTimeout(() => {
      void aiApi
        .updateExerciseSetProgress(exerciseSetId, JSON.stringify(snapshot))
        .catch(() => undefined)
    }, 600)
    return () => {
      if (progressSyncRef.current) {
        clearTimeout(progressSyncRef.current)
      }
    }
  }, [
    userId,
    lesson,
    model,
    activeSteps,
    currentIndex,
    answers,
    results,
    phase,
    startedAt,
    activeStudyMs,
    exerciseSetId,
  ])

  useEffect(() => {
    if (phase !== 'active') return

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (studyPausedRef.current) return
        const now = Date.now()
        const nextActive = activeStudyMsRef.current + Math.max(0, now - startedAtRef.current)
        activeStudyMsRef.current = nextActive
        setActiveStudyMs(nextActive)
        studyPausedRef.current = true
        return
      }
      if (!studyPausedRef.current) return
      const now = Date.now()
      startedAtRef.current = now
      setStartedAt(now)
      studyPausedRef.current = false
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [phase])

  useEffect(() => {
    if (phase !== 'summary' || !exerciseSetId || submittedRef.current) {
      return
    }
    submittedRef.current = true
    setSubmitError(null)
    const snapshot = buildSessionSnapshot(
      exerciseSetId,
      lesson,
      model,
      activeSteps,
      currentIndex,
      answers,
      results,
      phase,
      startedAtRef.current,
      activeStudyMsRef.current,
    )
    const studyTimeMin = studyDurationMinutes(
      activeStudyMsRef.current,
      startedAtRef.current,
      { paused: studyPausedRef.current },
    )
    void aiApi
      .submitExerciseSet(exerciseSetId, {
        correctCount: correctCount,
        totalCount: total,
        studyTimeMin: Math.max(1, studyTimeMin),
        progressJson: JSON.stringify({ ...snapshot, userId, savedAt: Date.now() }),
      })
      .then(() => onLessonComplete?.())
      .catch(() => {
        submittedRef.current = false
        setSubmitError('Could not save your results. Please try again.')
      })
  }, [
    userId,
    phase,
    exerciseSetId,
    lesson,
    model,
    activeSteps,
    currentIndex,
    answers,
    results,
    correctCount,
    total,
    onLessonComplete,
    submitAttempt,
  ])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [current?.exercise.id])

  const handleCheck = () => {
    if (!current) return
    const exercise = current.exercise
    const answer = answers[exercise.id] ?? ''
    if (!answer.trim()) {
      showHint(
        exercise.type === 'fill_blank'
          ? copy.exercise.fillBlankTypeAnswer
          : copy.exercise.multipleChoiceSelectOption,
      )
      return
    }
    clearHint()
    const correct = isExerciseCorrect(exercise, answer)
    setResults((prev) => ({
      ...prev,
      [exercise.id]: { checked: true, correct },
    }))
  }

  const applyAnswer = (exercise: LessonExercise, value: string, autoCheck: boolean) => {
    setAnswers((prev) => ({ ...prev, [exercise.id]: value }))
    clearHint()
    if (!autoCheck || !value.trim() || results[exercise.id]?.checked) {
      return
    }
    const correct = isExerciseCorrect(exercise, value)
    setResults((prev) => ({
      ...prev,
      [exercise.id]: { checked: true, correct },
    }))
  }

  useEffect(() => {
    clearHint()
  }, [current?.exercise.id, clearHint])

  const handleNext = () => {
    if (currentIndex < total - 1) {
      setCurrentIndex((i) => i + 1)
      return
    }
    setPhase('summary')
  }

  const handleRetryMistakes = () => {
    if (wrongSteps.length === 0) return
    submittedRef.current = false
    setSubmitError(null)
    setActiveSteps(wrongSteps)
    setCurrentIndex(0)
    setPhase('active')
    const nextResults: Record<string, StepResult> = {}
    const nextAnswers: Record<string, string> = {}
    for (const step of wrongSteps) {
      nextResults[step.exercise.id] = { checked: false, correct: null }
      nextAnswers[step.exercise.id] = answers[step.exercise.id] ?? ''
    }
    setResults(nextResults)
    setAnswers(nextAnswers)
  }

  const handleNewLesson = () => {
    clearLessonSession(userId)
    onNewLesson()
  }

  const handleRetrySubmit = () => {
    submittedRef.current = false
    setSubmitError(null)
    setSubmitAttempt((attempt) => attempt + 1)
  }

  const isOptionBasedExercise =
    current?.exercise.type === 'multiple_choice' ||
    (current?.exercise.type === 'fill_blank' && (current.exercise.options?.length ?? 0) > 0)

  const isFreeTextFillBlank =
    current?.exercise.type === 'fill_blank' && !isOptionBasedExercise

  const canCheck =
    current &&
    !currentResult?.checked &&
    Boolean((answers[current.exercise.id] ?? '').trim())

  const footerHint = current
    ? !currentResult?.checked
      ? isOptionBasedExercise
        ? current.exercise.type === 'fill_blank'
          ? copy.exercise.fillBlankSelectOption
          : copy.exercise.multipleChoiceSelectOption
        : copy.exercise.fillBlankTypeAnswer
      : copy.exercise.readyToContinue
    : ''

  const renderQuestion = () => {
    if (!current) return null
    return (
      <>
        {current.exercise.type === 'fill_blank' ? (
          <FillBlankQuestion
            questionText={current.exercise.prompt}
            options={current.exercise.options}
            value={answers[current.exercise.id] ?? ''}
            disabled={Boolean(currentResult?.checked)}
            onChange={(value) =>
              applyAnswer(current.exercise, value, isOptionBasedExercise)
            }
            onSubmit={handleCheck}
          />
        ) : null}

        {current.exercise.type === 'multiple_choice' ? (
          <MultipleChoiceQuestion
            questionText={current.exercise.prompt}
            options={current.exercise.options}
            selected={answers[current.exercise.id]}
            disabled={Boolean(currentResult?.checked)}
            onSelect={(value) => applyAnswer(current.exercise, value, true)}
          />
        ) : null}

        {currentResult?.checked ? (
          <div
            className={cn(
              'mt-4 flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm',
              currentResult.correct
                ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',
            )}
            data-testid="exercise-feedback"
          >
            {currentResult.correct ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <X className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div>
              <p className="font-medium">{currentResult.correct ? 'Correct' : 'Not quite'}</p>
              {!currentResult.correct ? (
                <>
                  <p className="mt-1">
                    Answer: <span className="font-semibold">{current.exercise.correctAnswer}</span>
                  </p>
                  <p className="mt-1 text-muted-foreground">{current.exercise.explanation}</p>
                </>
              ) : (
                <p className="mt-1 text-muted-foreground">{current.exercise.explanation}</p>
              )}
            </div>
          </div>
        ) : null}
      </>
    )
  }

  const renderFooterActions = (fullWidth: boolean) => (
    <>
      <ExerciseActionHint message={actionHint} />
      {!currentResult?.checked ? (
        isFreeTextFillBlank ? (
          <Button
            className={cn(
              fullWidth ? 'min-h-11 w-full' : 'w-auto shrink-0',
              !canCheck && 'pointer-events-auto opacity-50 saturate-[0.85]',
            )}
            aria-disabled={!canCheck}
            onClick={handleCheck}
            data-testid="lesson-check-answer"
          >
            {copy.exercise.checkAnswer}
          </Button>
        ) : null
      ) : (
        <Button
          className={cn(fullWidth ? 'min-h-11 w-full gap-2' : 'w-auto shrink-0 gap-2')}
          onClick={handleNext}
          data-testid="lesson-next"
        >
          {currentIndex >= total - 1 ? 'Finish lesson' : 'Next'}
          <ArrowRight className="h-4 w-4" />
        </Button>
      )}
    </>
  )

  if (phase === 'summary') {
    return (
      <div
        className={cn(
          'flex h-full min-h-0 flex-1 flex-col',
          isMobile ? 'bg-background' : 'gap-3',
          className,
        )}
        data-testid="lesson-summary"
      >
        {isMobile ? (
          <header className="shrink-0 border-b px-3 pb-3 pt-[max(0.5rem,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Lesson complete</p>
                <p className="truncate text-xs text-muted-foreground">{lesson.title}</p>
              </div>
            </div>
          </header>
        ) : null}
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            isMobile ? 'overflow-y-auto px-4 py-4' : 'justify-center gap-5',
          )}
        >
          <div className={cn(isMobile ? 'space-y-4' : 'rounded-xl border bg-card p-5')}>
            {!isMobile ? (
              <>
                <h3 className="text-lg font-semibold">Lesson complete</h3>
                <p className="mt-1 text-sm text-muted-foreground">{lesson.title}</p>
              </>
            ) : null}
            <p className="text-3xl font-bold text-foreground">
              {correctCount}/{total}
              <span className="ml-2 text-base font-normal text-muted-foreground">correct</span>
            </p>
            {submitError ? (
              <div className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <p>{submitError}</p>
                <button
                  type="button"
                  onClick={handleRetrySubmit}
                  className="mt-1 font-medium underline hover:no-underline"
                >
                  Retry saving
                </button>
              </div>
            ) : null}
            {wrongSteps.length > 0 ? (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-foreground">Review mistakes</p>
                {wrongSteps.map((step) => (
                  <div key={step.exercise.id} className="rounded-lg bg-muted/40 px-3 py-2 text-sm">
                    <p className="font-medium">{step.exercise.prompt}</p>
                    <p className="mt-1 text-muted-foreground">{step.exercise.explanation}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className={cn('flex gap-3', isMobile ? 'mt-auto flex-col pt-6' : 'flex-wrap')}>
            {wrongSteps.length > 0 ? (
              <Button
                className={isMobile ? 'min-h-11 w-full' : 'w-auto'}
                onClick={handleRetryMistakes}
                data-testid="lesson-retry-mistakes"
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Retry {wrongSteps.length} mistake{wrongSteps.length === 1 ? '' : 's'}
              </Button>
            ) : null}
            <Button
              className={cn(
                isMobile
                  ? 'min-h-11 w-full bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  : 'w-auto bg-secondary text-secondary-foreground hover:bg-secondary/80',
              )}
              onClick={handleNewLesson}
            >
              New lesson
            </Button>
          </div>
        </div>
        {isMobile ? (
          <div className="shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]" />
        ) : null}
      </div>
    )
  }

  if (!current) {
    return null
  }

  const sectionChanged =
    currentIndex === 0 || activeSteps[currentIndex - 1]?.sectionId !== current.sectionId

  if (isMobile) {
    const hasScenario = Boolean(lesson.contextPassage)
    const showScenarioPane = hasScenario && mobilePane === 'scenario'

    return (
      <div
        className={cn('flex h-full min-h-0 flex-1 flex-col bg-background', className)}
        data-testid="lesson-runner"
      >
        <header className="shrink-0 border-b px-3 pb-2.5 pt-[max(0.5rem,env(safe-area-inset-top))]">
          <div className="flex items-center gap-1">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Leave lesson"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            ) : null}
            <div className="min-w-0 flex-1 px-1">
              {showScenarioPane ? (
                <>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-primary">
                    {copy.exercise.readScenario}
                  </p>
                  <p className="truncate text-sm font-semibold">{lesson.title}</p>
                </>
              ) : (
                <>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-primary">
                    {current.sectionTitle}
                  </p>
                  <p className="text-sm font-semibold" data-testid="lesson-progress-label">
                    Exercise {displayExerciseNumber} / {total}
                    <span className="ml-2 font-normal text-muted-foreground">
                      {minutesRemaining > 0 ? `≈ ${minutesRemaining} min` : 'Almost done'}
                    </span>
                  </p>
                </>
              )}
            </div>
          </div>
          {!showScenarioPane ? (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
                data-testid="lesson-progress-bar"
              />
            </div>
          ) : null}
        </header>

        {showScenarioPane ? (
          <>
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
              data-testid="lesson-scenario-pane"
            >
              <p className="whitespace-pre-wrap text-base leading-relaxed text-foreground">
                {lesson.contextPassage}
              </p>
              <p className="mt-4 text-xs text-muted-foreground">
                All exercises are based on this text.
              </p>
            </div>
            <div
              className="shrink-0 border-t bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              data-testid="lesson-runner-footer"
            >
              <Button
                className="min-h-11 w-full gap-2"
                onClick={() => {
                  setMobilePane('questions')
                  scrollRef.current?.scrollTo({ top: 0 })
                }}
                data-testid="lesson-go-to-questions"
              >
                {copy.exercise.goToQuestions}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3"
            >
              {hasScenario ? (
                <button
                  type="button"
                  onClick={() => {
                    setMobilePane('scenario')
                    scrollRef.current?.scrollTo({ top: 0 })
                  }}
                  className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary"
                  data-testid="lesson-back-to-text"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {copy.exercise.backToText}
                </button>
              ) : null}

              {!sectionChanged ? null : (
                <p className="sr-only">{current.sectionTitle}</p>
              )}

              {renderQuestion()}
            </div>

            <div
              className="shrink-0 space-y-2 border-t bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              data-testid="lesson-runner-footer"
            >
              {(!currentResult?.checked && isFreeTextFillBlank) || currentResult?.checked ? (
                <>
                  <p className="text-center text-xs text-muted-foreground">{footerHint}</p>
                  {renderFooterActions(true)}
                </>
              ) : (
                <p className="pb-1 text-center text-xs text-muted-foreground">{footerHint}</p>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-3',
        'max-h-[min(100dvh,100%)]',
        className,
      )}
      data-testid="lesson-runner"
    >
      {lesson.contextPassage ? (
        <LessonContextPassage
          passage={lesson.contextPassage}
          className="max-h-[28vh] shrink-0 overflow-y-auto"
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
        <div className="shrink-0 border-b px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">{lesson.title}</h3>
              <p className="text-xs text-muted-foreground capitalize">
                {lesson.difficulty}
                {model ? ` · ${model}` : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-foreground" data-testid="lesson-progress-label">
                Exercise {displayExerciseNumber} / {total}
              </p>
              <p className="text-xs text-muted-foreground" data-testid="lesson-time-remaining">
                {minutesRemaining > 0 ? `≈ ${minutesRemaining} min remaining` : 'Almost done'}
              </p>
            </div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
              data-testid="lesson-progress-bar"
            />
          </div>
        </div>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">
          {sectionChanged ? (
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-primary">
              {current.sectionTitle}
            </p>
          ) : null}
          {renderQuestion()}
        </div>

        <div
          className="flex shrink-0 flex-col gap-1.5 border-t bg-card px-5 py-3"
          data-testid="lesson-runner-footer"
        >
          <ExerciseActionHint message={actionHint} />
          <div className="flex items-center justify-between gap-3">
            <p className="line-clamp-1 min-w-0 flex-1 text-xs text-muted-foreground">{footerHint}</p>
            {!currentResult?.checked ? (
              isFreeTextFillBlank ? (
                <Button
                  className={cn('w-auto shrink-0', !canCheck && 'pointer-events-auto opacity-50 saturate-[0.85]')}
                  aria-disabled={!canCheck}
                  onClick={handleCheck}
                  data-testid="lesson-check-answer"
                >
                  {copy.exercise.checkAnswer}
                </Button>
              ) : null
            ) : (
              <Button className="w-auto shrink-0 gap-2" onClick={handleNext} data-testid="lesson-next">
                {currentIndex >= total - 1 ? 'Finish lesson' : 'Next'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
